import { showNavigationDrawer, userActions } from '@/actions';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { Button } from '@/components/elements/forms/buttons';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import { ThreeDSComponent } from '@/components/modules/adyen';
import AddCard from '@/components/modules/adyen/AddCard';
import { ModalActionbar } from '@/components/modules/modals/Modal';
import CardOptionsModal from '@/components/modules/pages/betalningsmetoder/CardOptionsModal';
import PaymentCardListItem from '@/components/modules/pages/betalningsmetoder/PaymentCardListItem';
import EmptyState from '@/components/templates/EmptyState';
import { SCREEN_NAME, SCREEN_VIEW } from '@/constants/analytics';
import { useAccountNavigationContext } from '@/features/account-navigation/AccountNavigation.hooks';
import { promiseWrapper, trackPage, url } from '@/helpers';
import { getCheckoutClientErrorMessage } from '@/helpers/adyen';
import { buildBrowserInfo } from '@/helpers/googlepay';
import withWalletPayment from '@/hoc/withWalletPayment';
import { useAppSelector } from '@/hooks';
import { UseCardsProvider, useCardsContext, useCardsManager } from '@/hooks/adyen/useCards';
import { useAddAlert } from '@/hooks/useAlert';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import useTrackScreenShown, { trackScreenShown } from '@/hooks/useTrackScreenShown';
import { _s, _t } from '@/locale';
import { adyenServices } from '@/services';
import { CheckoutError, CofPaymentData, NewOnlineCardPaymentData } from '@/types/adyen';
import { PaymentCard } from '@/types/paymentcards';
import { PaymentData } from '@adyen/adyen-web/dist/types/components/types';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const PaymentMethods = () => {
  const user = useAppSelector((state) => state.users?.user);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PaymentCard>(null);
  const [threeDSAction, setThreeDSAction] = useState<string>(null);
  const loginContext = useContext(LoginContext);
  const history = useHistory();
  const { isMobileView } = useMobileView();
  const location = useLocation();
  const addAlert = useAddAlert();
  const dispatch = useDispatch();
  const { loading: cardLoading, error: cardError, loadCards, removeCard, cards, waitForCard } = useCardsContext();
  const accountNavigationContext = useAccountNavigationContext();

  useEffect(() => {
    trackPage();
    trackScreenShown({ screen_name: SCREEN_NAME.PAYMENT_METHODS });
  }, []);

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.ADD_PAYMENT_CARD_DETAILS, initiated_from: SCREEN_NAME.PAYMENT_METHODS },
    [isAddingCard],
    [isAddingCard],
  );

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.DELETE_PAYMENT_CARD, initiated_from: SCREEN_NAME.PAYMENT_METHODS },
    [showRemoveCardModal],
    [showRemoveCardModal],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('redirectResult')) {
      adyenServices.validate3DS({ details: { redirectResult: queryParams.get('redirectResult') } }).then((res) => {
        if (res.resultCode !== 'Authorised') {
          addAlert({ message: _s('addedNewCardFailed'), type: 'error', autoClose: true });
          return;
        }
        addAlert({ message: _s('addedNewCard'), type: 'success', autoClose: true });
      });
      queryParams.delete('sessionId');
      queryParams.delete('redirectResult');
      history.replace({
        search: queryParams.toString(),
      });
    }
  }, [history, location.search]);

  useEffect(() => {
    if (!cardLoading && cardError) {
      toast(({ closeToast }) => <Snackbar type="danger" label={_s('getStoredCardsError')} onClose={closeToast} />);
    }
  }, [cardLoading, cardError]);

  const handleRemoveCard = async (id: string) => {
    setShowRemoveCardModal(false);
    const isCardRemoved = await removeCard(id);
    if (!isCardRemoved) {
      toast(({ closeToast }) => <Snackbar type="danger" label={_s('removeCardError')} onClose={closeToast} />);
      return;
    }
    loadCards();
    toast(({ closeToast }) => (
      <Snackbar type="success" label={_s('cardRemoved')} action={<button onClick={closeToast}>OK</button>} />
    ));
  };

  const handleCloseRemoveCardModal = () => {
    setShowRemoveCardModal(false);
  };

  const handleOnSelect = (card: PaymentCard) => {
    setSelectedCard(card);
    setShowRemoveCardModal(true);
  };

  const handleOnAdditionalDetails = async (response) => {
    const { data, error } = await promiseWrapper(adyenServices.validate3DS({ details: response.data.details }));
    if (error || data?.resultCode !== 'Authorised') {
      toast(({ closeToast }) => (
        <Snackbar type="success" label={_s('checkoutError.StoreTokenError')} onClose={closeToast} />
      ));
    }
    setIsAddingCard(false);
    setThreeDSAction(null);
    waitForCard(data?.additionalData?.['recurring.recurringDetailReference']);
  };

  const handleOnAuthorize = async (paymentData: PaymentData) => {
    const paymentMethod = paymentData.paymentMethod;
    const parsedPaymentData: NewOnlineCardPaymentData = {
      type: 'newCard',
      encryptedCardNumber: paymentMethod.encryptedCardNumber,
      encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
      encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
      encryptedSecurityCode: paymentMethod.encryptedSecurityCode,
      holderName: paymentMethod.holderName,
    };

    const saveUserCardData: CofPaymentData = {
      paymentMethod: parsedPaymentData,
      redirectInfo: { returnPath: '/betalningsmetoder' },
      browserInfo: paymentData.browserInfo || buildBrowserInfo(),
    };

    const { data, error } = await promiseWrapper(adyenServices.saveUserCard({ paymentData: saveUserCardData }));

    if (data && data.requiresAction) {
      setThreeDSAction(data.adyenActionJson);
      return;
    }

    setIsAddingCard(false);

    if (error) {
      toast(({ closeToast }) => <Snackbar type="success" label={_s('serverError')} onClose={closeToast} />);
      return;
    }

    await waitForCard(data?.paymentDetails?.additionalData?.['recurring.recurringDetailReference']);
    toast(
      ({ closeToast }) => (
        <div className="absolute bottom-[94px] left-0 right-0 w-full">
          <Snackbar type="success" label={_s('addedNewCard')} action={<button onClick={closeToast}>OK</button>} />
        </div>
      ),
      {
        autoClose: false,
      },
    );
    // @ts-ignore
    dispatch(userActions.updateUser({ ...user, activeCoF: true }, true));
  };

  const handleStoreCardError = (error: CheckoutError) => {
    setIsAddingCard(false);
    addAlert({ message: getCheckoutClientErrorMessage(error), type: 'error', autoClose: true });
  };

  const handleAddNewCardClick = () => {
    setIsAddingCard(true);
  };

  const handleStoreCardDismiss = () => {
    setIsAddingCard(false);
  };

  const handleOnBackButtonClick = () => {
    if (isAddingCard) {
      handleStoreCardDismiss();
      return;
    }
    if (isMobileView) {
      dispatch(showNavigationDrawer({ show: true }));
    }
    if (location?.state?.fromAccountNavigation) {
      accountNavigationContext.onOpen();
    }
    history.goBack();
  };

  if (!user) {
    return (
      <PageViewLayout type="subView" title={_s('paymentMethods')}>
        <EmptyState
          title={_s('paymentMethods.emptyState.title')}
          body={_s('paymentMethods.emptyState.body')}
          icon={<img width={221} height={227} src="/images/illustrations/payment-methods2.png" alt="" />}
          cta={loginContext.renderLoginButton({
            label: _t('logIn'),
            size: 'sm',
            trackPromptEvent: true,
            initiatedFrom: 'direct_links',
          })}
        />
        <SEO {...seoPropsFromBaseString('paymentMethods', url.getBaseUrl() + 'betalningsmetoder')} />
      </PageViewLayout>
    );
  }

  const hasStoredCards = cards.length > 0;

  return (
    <PageViewLayout
      type="subView"
      back
      onBackButtonClick={handleOnBackButtonClick}
      title={isAddingCard ? _s('addNewPaymentCard') : _s('paymentMethods')}
      source="betalningsmetoder"
      wrapperClass="min-h-screen bg-brown-50">
      {cardLoading && <LoadingPlaceHolder />}

      {!cardLoading && (
        <Fragment>
          {selectedCard && (
            <CardOptionsModal
              isOpen={showRemoveCardModal}
              onClose={handleCloseRemoveCardModal}
              onRemove={handleRemoveCard}
              selectedCard={selectedCard}
            />
          )}

          <Fragment>
            {threeDSAction && isAddingCard && (
              <div className="h-[calc(100vh-64px)]">
                <ThreeDSComponent
                  action={JSON.parse(threeDSAction)}
                  handleOnAdditionalDetails={handleOnAdditionalDetails}
                />
              </div>
            )}

            {isAddingCard && !threeDSAction && (
              <div className="px-lg pt-lg bg-white">
                <div className="mx-auto w-full max-w-screen-md">
                  <AddCard
                    onAuthorize={handleOnAuthorize}
                    onError={handleStoreCardError}
                    submitLabel={_s('addCard')}
                    disclaimerLabel={_s('pages.betalningsmetoder.addCardDisclaimer')}
                  />
                </div>
              </div>
            )}

            {!isAddingCard && !threeDSAction && (
              <Fragment>
                {hasStoredCards ? (
                  <Fragment>
                    <div className="bg-white">
                      <div className="mx-auto w-full max-w-screen-md">
                        {cards.map((card) => {
                          return <PaymentCardListItem key={card.id} card={card} onSelect={handleOnSelect} />;
                        })}
                      </div>
                    </div>
                    <ModalActionbar className="!z-10">
                      <div className="mx-auto w-full max-w-screen-md">
                        <Button
                          label={_s('addNewCard')}
                          leftIcon={<Icon variant="plus" />}
                          onClick={handleAddNewCardClick}
                          size="md"
                          variant="primary"
                          block
                        />
                      </div>
                    </ModalActionbar>
                  </Fragment>
                ) : (
                  <div className="flex min-h-[calc(100vh-64px)] flex-col items-center justify-center">
                    <EmptyState
                      title={_s('pages.betalningsmetoder.noCardState.title')}
                      body={_s('pages.betalningsmetoder.noCardState.subTitle')}
                      icon={<img src="/images/illustrations/payment-methods2.png" alt="" />}
                      cta={
                        <Button
                          label={_s('pages.betalningsmetoder.noCardState.cta')}
                          size="sm"
                          onClick={handleAddNewCardClick}
                          leftIcon={<Icon variant="plus" />}
                          data-cy="add-stored-payment-card-button"
                        />
                      }
                    />
                  </div>
                )}
              </Fragment>
            )}
          </Fragment>
        </Fragment>
      )}

      <SEO {...seoPropsFromBaseString('paymentMethods', url.getBaseUrl() + 'betalningsmetoder')} />
    </PageViewLayout>
  );
};

const PaymentMethodsWithProviders = () => {
  const user = useAppSelector((state) => state.users?.user);
  const { cards, loading: cardLoading } = useCardsManager([], !!user);

  if (cardLoading) return <LoadingPlaceHolder />;

  return (
    <UseCardsProvider initialCards={cards}>
      <PaymentMethods></PaymentMethods>
    </UseCardsProvider>
  );
};

export default withWalletPayment(PaymentMethodsWithProviders);
